// src/pages/AddGame.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, addDoc, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from '../contexts/authContext';
import { useNavigate, Navigate, useParams } from 'react-router-dom';
import HostLayout from '../layouts/HostLayout';
import { useLocation } from 'react-router-dom';
import Logo from '../components/Logo';
import ImageUploader from '../components/ImageUploader';
import { v4 as uuidv4 } from 'uuid';
import { reviewGameContent, shouldAutoApprove, shouldAutoReject, getModerationDisplay, isPotentialFalsePositive } from '../utils/aiModeration';
// Removed subscription-related imports - all users can now submit to marketplace
import { getApps, getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';

const AddGame = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: gameId } = useParams(); // Get game ID from URL for editing
  const { user, ready: authReady, claims } = useAuth();
  
  // Null-safe helpers for arrays that may contain nulls, strings, or user objects
  const toArray = (v) => (Array.isArray(v) ? v : []);
  const compact = (arr) => toArray(arr).filter(Boolean);
  
  // Get UIDs from mixed arrays (objects with .uid or plain strings) - removed unused toUids
  
  // Sane read for current user uid
  const authUid = () => user?.uid ?? null;
  const isAdminRoute = location.pathname.startsWith('/admin/');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [userRole, setUserRole] = useState(null);
  // Removed creatorLimits - no subscription limits anymore
  const tempGameIdRef = useRef(uuidv4());
  const titleInputRef = useRef(null);
  
  // Memoized handlers to prevent re-renders - removed unused handleTitleChange

  const handleQuarterChange = useCallback((quarter, index, field, value) => {
    setGameForm(prev => ({
      ...prev,
      [quarter]: prev[quarter].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  }, []);

  const handleSectionChange = useCallback((section, field, value) => {
    setGameForm(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  }, []);

  const [gameForm, setGameForm] = useState({
    title: '',
    description: '',
    price: 0,
    categories: [],
    difficulty: 'medium',
    isForMarketplace: false,
    isLeagueSponsored: false,
    hostingNotes: '',
    // Game content sections
    quarter1: Array(5).fill().map(() => ({
      category: '',
      question: '',
      questionInstruction: '',
      leftColumn: '',
      rightColumn: '',
      answerPairs: [{}, {}, {}, {}],
      orderingItems: '',
      answer: '',
      questionImage: '',
      answerImage: '',
      type: 'standard',
      creatorComment: ''
    })),
    quarter2: Array(5).fill().map(() => ({
      category: '',
      question: '',
      questionInstruction: '',
      leftColumn: '',
      rightColumn: '',
      answerPairs: [{}, {}, {}, {}],
      orderingItems: '',
      answer: '',
      questionImage: '',
      answerImage: '',
      type: 'standard',
      creatorComment: ''
    })),
    quarter3: Array(5).fill().map(() => ({
      category: '',
      question: '',
      questionInstruction: '',
      leftColumn: '',
      rightColumn: '',
      answerPairs: [{}, {}, {}, {}],
      orderingItems: '',
      answer: '',
      questionImage: '',
      answerImage: '',
      type: 'standard',
      creatorComment: ''
    })),
    quarter4: Array(5).fill().map(() => ({
      category: '',
      question: '',
      questionInstruction: '',
      leftColumn: '',
      rightColumn: '',
      answerPairs: [{}, {}, {}, {}],
      orderingItems: '',
      answer: '',
      questionImage: '',
      answerImage: '',
      type: 'standard',
      creatorComment: ''
    })),
    halftime: {
      category: '',
      question: '',
      questionInstruction: '',
      leftColumn: '',
      rightColumn: '',
      answerPairs: [{}, {}, {}, {}],
      orderingItems: '',
      answer: '',
      questionImage: '',
      answerImage: '',
      type: 'standard',
      creatorComment: ''
    },
    final: {
      category: '',
      question: '',
      questionInstruction: '',
      leftColumn: '',
      rightColumn: '',
      answerPairs: [{}, {}, {}, {}],
      orderingItems: '',
      answer: '',
      questionImage: '',
      answerImage: '',
      type: 'standard',
      creatorComment: ''
    },
    tieBreaker: {
      category: '',
      question: '',
      questionInstruction: '',
      leftColumn: '',
      rightColumn: '',
      answerPairs: [{}, {}, {}, {}],
      orderingItems: '',
      answer: '',
      questionImage: '',
      answerImage: '',
      type: 'standard',
      creatorComment: ''
    },
    // Ad sections
    adImagesStart: [],
    adImagesAfterHalftime: [],
    adImagesAfterFinal: []
  });

  // Get user role from the user object (already fetched by useAuth)
  useEffect(() => {
    if (user && !userRole) {
      setUserRole(user?.role || 'host');
    }
    // No subscription limits - all users can create and submit games
  }, [user, userRole, isAdminRoute]);

  // Load existing draft when editing
  useEffect(() => {
    const loadExistingDraft = async () => {
      if (gameId && user?.uid && authReady) {
        try {
          console.log('Loading existing draft:', gameId);
          const draftRef = doc(db, `users/${user.uid}/drafts/${gameId}`);
          const draftSnap = await getDoc(draftRef);
          
          if (draftSnap.exists()) {
            const draftData = draftSnap.data();
            console.log('Loaded draft data:', draftData);
            
            // Update the form with existing draft data
            setGameForm(prev => ({
              ...prev,
              id: gameId,
              title: draftData.title || '',
              sections: draftData.sections || [],
              assets: draftData.assets || {},
              isForMarketplace: false, // Always start as draft
              isLeagueSponsored: draftData.isLeagueSponsored || false
            }));
            
            setMessage('✅ Draft loaded successfully!');
          } else {
            console.warn('Draft not found:', gameId);
            setMessage('❌ Draft not found. Creating new game.');
          }
        } catch (error) {
          console.error('Error loading draft:', error);
          setMessage('❌ Error loading draft. Creating new game.');
        }
      }
    };

    loadExistingDraft();
  }, [gameId, user?.uid, authReady]);

  // Available categories
  const availableCategories = [
    'History', 'Science', 'Geography', 'Sports', 'Entertainment', 
    'Literature', 'Music', 'Movies & TV', 'Technology', 'Art', 
    'Politics', 'Business', 'Food & Drink', 'Nature', 'Math',
    'Pop Culture', 'Mythology', 'Religion', 'Fashion', 'Other'
  ];

  // Debug Firebase identity to detect duplicate apps
  React.useEffect(() => {
    (function debugFirebaseIdentity(tag) {
      try {
        const apps = getApps();
        const app = getApp();
        const auth = getAuth(app);
        console.log(`[FB-ID:${tag}]`, {
          apps: apps.map(a => a.name),
          appName: app.name,
          projectId: app.options?.projectId,
          currentUser: !!auth.currentUser,
        });
      } catch (e) { 
        console.log(`[FB-ID:${tag}] error`, e?.message); 
      }
    })("add-game");
  }, []);

  // Debug auth state
  console.log("AddGame auth state:", { authReady, hasUser: !!user, path: location.pathname });

  // Self-sufficient auth check - never sign out, just show loading or redirect
  if (!authReady) {
    return <div className="p-6 text-sm opacity-70">Loading…</div>;
  }
  
  if (!user) {
    // Small inline login redirect - no signOut call
    console.log("AddGame: No user, redirecting to login");
    return <Navigate to={`/login?next=${encodeURIComponent('/add-game')}`} replace />;
  }

  // Guard early so we render instead of crashing
  const uid = authUid();
  if (!uid) {
    // Inline fallback; DO NOT navigate or signOut here
    return <div className="p-6">Reconnecting… Try again.</div>;
  }

  const handleCategoryToggle = (category) => {
    setGameForm(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };



  const handleQuarterImageRemove = (quarter, index, field) => {
    setGameForm(prev => ({
      ...prev,
      [quarter]: prev[quarter].map((item, i) => 
        i === index ? { ...item, [field]: '' } : item
      )
    }));
  };



  const handleSectionImageRemove = (section, field) => {
    setGameForm(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: '' }
    }));
  };

  const validateGame = () => {
    const errors = [];

    if (!gameForm.title.trim()) {
      errors.push('Game title is required');
    }

    if (gameForm.isForMarketplace && gameForm.price < 20) {
      errors.push('Marketplace games must have a price of at least $20.00');
    }

    if (gameForm.categories.length === 0) {
      errors.push('At least one category is required');
    }

    // Validate all quarters have content
    ['quarter1', 'quarter2', 'quarter3', 'quarter4'].forEach(quarter => {
      compact(gameForm[quarter]).forEach((item, index) => {
        if (!item.category) {
          errors.push(`${quarter.charAt(0).toUpperCase() + quarter.slice(1)} Question ${index + 1} category is required`);
        }
        
        if (item.type === 'matching') {
          if (!item.questionInstruction || !item.leftColumn || !item.rightColumn) {
            errors.push(`${quarter.charAt(0).toUpperCase() + quarter.slice(1)} Question ${index + 1} matching question is incomplete (need instruction, left column, and right column)`);
          }
        } else if (item.type === 'ordering') {
          if (!item.questionInstruction || !item.orderingItems) {
            errors.push(`${quarter.charAt(0).toUpperCase() + quarter.slice(1)} Question ${index + 1} ordering question is incomplete (need instruction and items to order)`);
          }
        } else {
          if (!item.question) {
            errors.push(`${quarter.charAt(0).toUpperCase() + quarter.slice(1)} Question ${index + 1} question is required`);
          }
        }
        
        if (!item.answer) {
          errors.push(`${quarter.charAt(0).toUpperCase() + quarter.slice(1)} Question ${index + 1} answer is required`);
        }
      });
    });

    // Validate special sections
    ['halftime', 'final', 'tieBreaker'].forEach(section => {
      const item = gameForm[section];
      if (!item.category) {
        errors.push(`${section.charAt(0).toUpperCase() + section.slice(1)} section category is required`);
      }
      
      if (item.type === 'matching') {
        if (!item.questionInstruction || !item.leftColumn || !item.rightColumn) {
          errors.push(`${section.charAt(0).toUpperCase() + section.slice(1)} section matching question is incomplete (need instruction, left column, and right column)`);
        }
      } else if (item.type === 'ordering') {
        if (!item.questionInstruction || !item.orderingItems) {
          errors.push(`${section.charAt(0).toUpperCase() + section.slice(1)} section ordering question is incomplete (need instruction and items to order)`);
        }
      } else {
        if (!item.question) {
          errors.push(`${section.charAt(0).toUpperCase() + section.slice(1)} section question is required`);
        }
      }
      
      if (!item.answer) {
        errors.push(`${section.charAt(0).toUpperCase() + section.slice(1)} section answer is required`);
      }
    });

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is admin - bypass moderation for admins
    const isAdminUser = claims?.admin === true;
    
    const errors = validateGame();
    if (errors.length > 0) {
      setMessage('❌ ' + errors.join(', '));
      return;
    }

    setLoading(true);
    setMessage('🤖 Reviewing content with AI...');

    try {
      // All users can now submit to marketplace - no subscription required
      
      let moderationResult;
      
      // Only run AI moderation if submitting to marketplace
      if (gameForm.isForMarketplace) {
        if (isAdminUser) {
          // Bypass AI moderation for admin users
          moderationResult = {
            category: 'safe',
            severity: 'low',
            approved: true,
            issues: [],
            recommendations: 'Content approved (Admin user - moderation bypassed)',
            confidence: 1.0,
            flaggedItems: []
          };
          setMessage('✅ Admin user - bypassing moderation...');
        } else {
          // AI Content Moderation for marketplace submissions
          // Set to true to bypass AI moderation for testing, false for production
          const skipAIModeration = false; // AI moderation enabled for production
          
          if (skipAIModeration) {
            // Bypass AI moderation - approve everything
            moderationResult = {
              category: 'safe',
              severity: 'low',
              approved: true,
              issues: [],
              recommendations: 'Content approved (AI moderation bypassed for testing)',
              confidence: 1.0,
              flaggedItems: []
            };
          } else {
            moderationResult = await reviewGameContent(gameForm);
          }
        }
      } else {
        // No moderation needed for personal games
        moderationResult = {
          category: 'safe',
          severity: 'low',
          approved: true,
          issues: [],
          recommendations: 'Content approved (Personal game - no moderation required)',
          confidence: 1.0,
          flaggedItems: []
        };
      }
      
      // Debug: Log the full moderation result
      console.log('Full moderation result:', JSON.stringify(moderationResult, null, 2));
      
      // Check if content should be auto-rejected (skip for admin users)
      if (!isAdminUser && shouldAutoReject(moderationResult)) {
        const display = getModerationDisplay(moderationResult);
        let detailedMessage = `❌ ${display.text}: ${moderationResult.recommendations}`;
        
        // Add detailed feedback about flagged content
        if (moderationResult.flaggedItems && moderationResult.flaggedItems.length > 0) {
          detailedMessage += '\n\n🚨 Flagged Content Details:';
          moderationResult.flaggedItems.forEach((item, index) => {
            detailedMessage += `\n${index + 1}. Field: ${item.field}`;
            detailedMessage += `\n   Content: "${item.content}"`;
            detailedMessage += `\n   Reason: ${item.reason}`;
          });
        } else {
          detailedMessage += '\n\n⚠️ No specific flagged items provided by AI moderation.';
        }
        
        // Add specific issues if available
        if (moderationResult.issues && moderationResult.issues.length > 0) {
          detailedMessage += '\n\n⚠️ Specific Issues:';
          moderationResult.issues.forEach((issue, index) => {
            detailedMessage += `\n${index + 1}. ${issue.description}`;
            if (issue.flaggedContent) {
              detailedMessage += `\n   Flagged: "${issue.flaggedContent}"`;
            }
            detailedMessage += `\n   Suggestion: ${issue.suggestion}`;
          });
        } else {
          detailedMessage += '\n\n⚠️ No specific issues provided by AI moderation.';
        }
        
        // Add category and severity info
        detailedMessage += `\n\n📊 Moderation Details:`;
        detailedMessage += `\n- Category: ${moderationResult.category}`;
        detailedMessage += `\n- Severity: ${moderationResult.severity}`;
        detailedMessage += `\n- Confidence: ${Math.round((moderationResult.confidence || 0) * 100)}%`;
        
        // Add note about potential false positive
        if (moderationResult.category === 'inappropriate' && moderationResult.confidence < 0.8) {
          detailedMessage += '\n\n💡 Note: This may be a false positive. If you believe your content is appropriate, you can submit it for manual review by creating the game for personal use first.';
        }
        
        // Check for potential false positive using the detection function
        if (isPotentialFalsePositive(moderationResult, gameForm)) {
          detailedMessage += '\n\n🔍 False Positive Detection: This content appears to contain normal trivia topics and may have been incorrectly flagged. Consider creating the game for personal use first, then contact support for manual review.';
        }
        
        setMessage(detailedMessage);
        setLoading(false);
        return;
      }

      // Determine approval status based on moderation and user role
      let approvalStatus = 'pending';
      let status = 'private';
      
      if (gameForm.isForMarketplace) {
        if (isAdminUser || shouldAutoApprove(moderationResult)) {
          approvalStatus = 'approved';
          status = 'approved';
          setMessage(isAdminUser ? '✅ Admin user - content approved! Creating game...' : '✅ Content approved! Creating game...');
        } else {
          approvalStatus = 'pending';
          status = 'pending_approval';
          setMessage('⏳ Content flagged for manual review. Creating game...');
        }
      }



      const gameData = {
        ...gameForm,
        creatorId: user?.uid || 'temp',
        creatorName: user?.displayName || user?.email || 'Unknown',
        status: status,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // League sponsored flag (admin only)
        isLeagueSponsored: gameForm.isLeagueSponsored || false,
        // AI Moderation data
        moderation: {
          ...moderationResult,
          autoApproved: shouldAutoApprove(moderationResult),
          autoRejected: shouldAutoReject(moderationResult)
        },
        // Marketplace data (will be updated later based on user role)
        marketplaceData: gameForm.isForMarketplace ? {
          price: gameForm.price,
          salesCount: 0,
          totalRevenue: 0,
          creatorRevenue: 0,
          triviaPlugRevenue: 0,
          approvalStatus: approvalStatus, // Use the determined approval status
          approvedAt: null,
          approvedBy: null,
          isApproved: false
        } : null,
        // Usage tracking
        usageCount: 0,
        lastUsed: null
      };



      // Always save drafts to user's profile first
      const uid = user?.uid;
      if (!uid) {
        console.warn("No auth user at submit time");
        setMessage("❌ Error: Not authenticated. Please try again.");
        return;
      }

      // Always save to main games collection first (for user's Created Games)
      const personalGamePayload = {
        ...gameData,
        ownerId: uid,
        visibility: "private",           // Personal game
        status: "created",               // Created status
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      console.log("Saving game to main games collection:", { keys: Object.keys(personalGamePayload) });

      // Save to main games collection (user's Created Games)
      const personalGameRef = await addDoc(collection(db, 'games'), personalGamePayload);
      console.log("✅ Game saved to main games collection:", personalGameRef.id);

      // If submitting to marketplace, also create a separate copy for marketplace approval
      if (gameForm.isForMarketplace) {
        // Determine approval status based on user role
        const isAdminUser = claims?.admin === true;
        const approvalStatus = isAdminUser ? 'approved' : 'pending';
        const gameStatus = isAdminUser ? 'approved' : 'pending_approval';
        
        // Create marketplace copy with different data
        const marketplacePayload = {
          ...gameData,
          ownerId: uid,
          visibility: "marketplace",
          status: gameStatus,
          marketplaceData: {
            ...gameData.marketplaceData,
            approvalStatus: approvalStatus,
            approvedAt: isAdminUser ? serverTimestamp() : null,
            approvedBy: isAdminUser ? user.uid : null,
            isApproved: isAdminUser,
            submittedAt: serverTimestamp(),
            submittedBy: uid
          },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        console.log("Saving marketplace copy:", { 
          keys: Object.keys(marketplacePayload),
          isAdminUser,
          approvalStatus,
          gameStatus
        });

        // Save to marketplace collection (for approval)
        const marketplaceRef = await addDoc(collection(db, 'games', 'content', 'triviaGames'), marketplacePayload);
        console.log("✅ Game saved to marketplace collection:", marketplaceRef.id);

        // Update the personal game to link to marketplace version
        await updateDoc(doc(db, 'games', personalGameRef.id), {
          marketplaceStatus: 'submitted',
          marketplaceGameId: marketplaceRef.id,
          submittedAt: serverTimestamp(),
          submittedBy: uid
        });
        
        // Success message based on user role
        if (isAdminUser) {
          setMessage(`✅ Game saved to Created Games and added to marketplace immediately! (Admin auto-approval)`);
        } else {
          setMessage(`✅ Game saved to Created Games and submitted to marketplace for approval!`);
        }
      } else {
        // Success message - game saved to created games only
        setMessage(`✅ Game saved to your Created Games! You can now launch it or submit it to the marketplace for approval.`);
      }
      
      setTimeout(() => {
        navigate(isAdminRoute ? '/admin' : '/home');
      }, 3000);
    } catch (error) {
      console.error('Error creating game:', error);
      setMessage('❌ Failed to create game. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderQuarter = (quarter, label) => {
    const quarterData = gameForm[quarter];
    
    // Log bad data to find the source
    if (quarterData && quarterData.some((x) => x == null)) {
      console.warn("Null entries in quarter data:", quarter, quarterData);
    }
    
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-bold text-white mb-4">{label}</h2>
        {compact(quarterData).map((item, index) => (
        <div key={index} className="bg-gray-700 rounded-lg p-4 mb-4">
          <h3 className="text-lg font-semibold text-white mb-3">Question {index + 1}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-white mb-2">Category</label>
              <input
                key={`${quarter}-${index}-category-stable`}
                type="text"
                defaultValue={item.category}
                onBlur={(e) => handleQuarterChange(quarter, index, 'category', e.target.value)}
                className="w-full p-3 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none"
                placeholder="Type category (e.g., History, Science, Pop Culture)"
                list="category-suggestions"
              />
              <datalist id="category-suggestions">
                {availableCategories.map(cat => (
                  <option key={cat} value={cat} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-white mb-2">Question Type</label>
              <select
                value={item.type}
                onChange={(e) => handleQuarterChange(quarter, index, 'type', e.target.value)}
                className="w-full p-3 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none"
              >
                <option value="standard">Standard</option>
                <option value="matching">Matching</option>
                <option value="ordering">Ordering</option>
              </select>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-white mb-2">Question</label>
              {item.type === 'matching' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-white mb-2 text-sm">Instruction</label>
                    <textarea
                      key={`${quarter}-${index}-instruction-stable`}
                      defaultValue={item.questionInstruction}
                      onBlur={(e) => handleQuarterChange(quarter, index, 'questionInstruction', e.target.value)}
                      className="w-full p-3 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none"
                      rows="2"
                      placeholder="e.g., Match the US state to its nickname"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white mb-2 text-sm">Left Column (A, B, C, D...)</label>
                      <textarea
                        key={`${quarter}-${index}-leftcolumn-stable`}
                        defaultValue={item.leftColumn}
                        onBlur={(e) => handleQuarterChange(quarter, index, 'leftColumn', e.target.value)}
                        className="w-full p-3 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none"
                        rows="4"
                        placeholder="A. Alabama&#10;B. Colorado&#10;C. Idaho&#10;D. North Dakota"
                      />
                    </div>
                    <div>
                      <label className="block text-white mb-2 text-sm">Right Column (1, 2, 3, 4...)</label>
                      <textarea
                        key={`${quarter}-${index}-rightcolumn-stable`}
                        defaultValue={item.rightColumn}
                        onBlur={(e) => handleQuarterChange(quarter, index, 'rightColumn', e.target.value)}
                        className="w-full p-3 bg-gray-600 text-white rounded border border-gray-500 focus:outline-none"
                        rows="4"
                        placeholder="1. The Peace Garden State&#10;2. The Yellowhammer State&#10;3. The Gem State&#10;4. The Centennial State"
                      />
                    </div>
                  </div>
                  {/* Correct Answer Pairs (optional but recommended) */}
                  <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                    <label className="block text-white mb-2 text-sm">Correct Answer Pairs</label>
                    <div className="space-y-2">
                      {(item.answerPairs || [{}, {}, {}, {}]).map((pair, pairIndex) => (
                        <div key={pairIndex} className="flex gap-2 items-center">
                          <input
                            type="text"
                            className="flex-1 p-2 bg-gray-600 text-white rounded border border-gray-500 placeholder-gray-400"
                            placeholder={`Left item ${pairIndex + 1}`}
                            defaultValue={pair.left || ''}
                            onBlur={(e) => {
                              const currentPairs = (item.answerPairs || [{}, {}, {}, {}]).slice();
                              currentPairs[pairIndex] = { ...currentPairs[pairIndex], left: e.target.value };
                              handleQuarterChange(quarter, index, 'answerPairs', currentPairs);
                            }}
                          />
                          <span className="text-white">→</span>
                          <input
                            type="text"
                            className="flex-1 p-2 bg-gray-600 text-white rounded border border-gray-500 placeholder-gray-400"
                            placeholder={`Right item ${pairIndex + 1}`}
                            defaultValue={pair.right || ''}
                            onBlur={(e) => {
                              const currentPairs = (item.answerPairs || [{}, {}, {}, {}]).slice();
                              currentPairs[pairIndex] = { ...currentPairs[pairIndex], right: e.target.value };
                              handleQuarterChange(quarter, index, 'answerPairs', currentPairs);
                            }}
                          />
                          <button
                            type="button"
                            className="bg-red-600 text-white px-2 py-1 rounded text-sm hover:bg-red-700"
                            onClick={() => {
                              const currentPairs = (item.answerPairs || [{}, {}, {}, {}]).slice();
                              currentPairs.splice(pairIndex, 1);
                              handleQuarterChange(quarter, index, 'answerPairs', currentPairs);
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                        onClick={() => {
                          const currentPairs = (item.answerPairs || [{}, {}, {}, {}]).slice();
                          currentPairs.push({});
                          handleQuarterChange(quarter, index, 'answerPairs', currentPairs);
                        }}
                      >
                        + Add Pair
                      </button>
                    </div>
                  </div>
                </div>
              ) : item.type === 'ordering' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-white mb-2 text-sm">Instruction</label>
                  <textarea
                    key={`${quarter}-${index}-ordering-instruction`}
                    defaultValue={item.questionInstruction}
                    onBlur={(e) => handleQuarterChange(quarter, index, 'questionInstruction', e.target.value)}
                    className="w-full p-3 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none"
                    rows="2"
                    placeholder="e.g., Put these in order from earliest to latest"
                  />
                  </div>
                  <div>
                    <label className="block text-white mb-2 text-sm">Items to Order (one per line)</label>
                  <textarea
                    key={`${quarter}-${index}-ordering-items`}
                    defaultValue={item.orderingItems}
                    onBlur={(e) => handleQuarterChange(quarter, index, 'orderingItems', e.target.value)}
                    className="w-full p-3 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none"
                    rows="4"
                    placeholder="Item A\nItem B\nItem C\nItem D"
                  />
                  </div>
                </div>
              ) : (
                <textarea
                  key={`${quarter}-${index}-question-stable`}
                  defaultValue={item.question}
                  onBlur={(e) => handleQuarterChange(quarter, index, 'question', e.target.value)}
                  className="w-full p-3 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none"
                  rows="3"
                  placeholder="Enter your question..."
                />
              )}
              <ImageUploader
                label="Upload Question Image (optional)"
                storagePath={`game-images/${user?.uid || 'temp'}/${tempGameIdRef.current}/${quarter}-q${index + 1}-question`}
                onUpload={(url) => handleQuarterChange(quarter, index, 'questionImage', url)}
              />
              {item.questionImage && (
                <div className="relative mt-2">
                  <img src={item.questionImage} alt="Question" className="max-h-32 rounded border" />
                  <button
                    type="button"
                    className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
                    onClick={() => handleQuarterImageRemove(quarter, index, 'questionImage')}
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className="block text-white mb-2">Answer</label>
              <input
                key={`${quarter}-${index}-answer-stable`}
                type="text"
                defaultValue={item.answer}
                onBlur={(e) => handleQuarterChange(quarter, index, 'answer', e.target.value)}
                className="w-full p-3 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none"
                placeholder="Enter the answer..."
              />
              <ImageUploader
                label="Upload Answer Image (optional)"
                storagePath={`game-images/${user?.uid || 'temp'}/${tempGameIdRef.current}/${quarter}-q${index + 1}-answer`}
                onUpload={(url) => handleQuarterChange(quarter, index, 'answerImage', url)}
              />
              {item.answerImage && (
                <div className="relative mt-2">
                  <img src={item.answerImage} alt="Answer" className="max-h-32 rounded border" />
                  <button
                    type="button"
                    className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
                    onClick={() => handleQuarterImageRemove(quarter, index, 'answerImage')}
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
            
            {/* Creator Comment for this question */}
            <div>
              <label className="block text-white mb-2">
                💡 Creator Comment (Optional)
              </label>
              <textarea
                key={`${quarter}-${index}-creator-comment-stable`}
                defaultValue={item.creatorComment}
                onBlur={(e) => handleQuarterChange(quarter, index, 'creatorComment', e.target.value)}
                className="w-full p-3 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none"
                rows="3"
                placeholder="Explain hints, song clues, or provide context for this specific question..."
              />
              <p className="text-sm text-gray-400 mt-1">
                Optional: Explain how song hints relate to the answer, provide context, or share inside jokes
              </p>
            </div>
          </div>
        </div>
      ))}
      </div>
    );
  };

  const renderSpecialSection = (section, label) => (
    <div className="bg-gray-800 rounded-lg p-6">
      <h2 className="text-xl font-bold text-white mb-4">{label}</h2>
      <div className="bg-gray-700 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-white mb-2">Category</label>
            <input
              key={`${section}-category-stable`}
              type="text"
              defaultValue={gameForm[section].category}
              onBlur={(e) => handleSectionChange(section, 'category', e.target.value)}
              className="w-full p-3 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none"
              placeholder="Type category (e.g., History, Science, Pop Culture)"
              list="category-suggestions"
            />
            <datalist id="category-suggestions">
              {availableCategories.map(cat => (
                <option key={cat} value={cat} />
              ))}
            </datalist>
          </div>
          <div>
            <label className="block text-white mb-2">Question Type</label>
            <select
              value={gameForm[section].type}
              onChange={(e) => handleSectionChange(section, 'type', e.target.value)}
              className="w-full p-3 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none"
            >
              <option value="standard">Standard</option>
              <option value="matching">Matching</option>
              <option value="ordering">Ordering</option>
            </select>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-white mb-2">Question</label>
            {gameForm[section].type === 'matching' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-white mb-2 text-sm">Instruction</label>
                  <textarea
                    key={`${section}-instruction-stable`}
                    defaultValue={gameForm[section].questionInstruction}
                    onBlur={(e) => handleSectionChange(section, 'questionInstruction', e.target.value)}
                    className="w-full p-3 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none"
                    rows="2"
                    placeholder="e.g., Match the US state to its nickname"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white mb-2 text-sm">Left Column (A, B, C, D...)</label>
                    <textarea
                      key={`${section}-leftcolumn-stable`}
                      defaultValue={gameForm[section].leftColumn}
                      onBlur={(e) => handleSectionChange(section, 'leftColumn', e.target.value)}
                      className="w-full p-3 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none"
                      rows="4"
                      placeholder="A. Alabama&#10;B. Colorado&#10;C. Idaho&#10;D. North Dakota"
                    />
                  </div>
                  <div>
                    <label className="block text-white mb-2 text-sm">Right Column (1, 2, 3, 4...)</label>
                    <textarea
                      key={`${section}-rightcolumn-stable`}
                      defaultValue={gameForm[section].rightColumn}
                      onBlur={(e) => handleSectionChange(section, 'rightColumn', e.target.value)}
                      className="w-full p-3 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none"
                      rows="4"
                      placeholder="1. The Peace Garden State&#10;2. The Yellowhammer State&#10;3. The Gem State&#10;4. The Centennial State"
                    />
                  </div>
                </div>
                {/* Correct Answer Pairs (optional but recommended) */}
                <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                  <label className="block text-white mb-2 text-sm">Correct Answer Pairs</label>
                  <div className="space-y-2">
                    {(gameForm[section].answerPairs || [{}, {}, {}, {}]).map((pair, pairIndex) => (
                      <div key={pairIndex} className="flex gap-2 items-center">
                        <input
                          type="text"
                          className="flex-1 p-2 bg-gray-600 text-white rounded border border-gray-500 placeholder-gray-400"
                          placeholder={`Left item ${pairIndex + 1}`}
                          defaultValue={pair.left || ''}
                          onBlur={(e) => {
                            const currentPairs = (gameForm[section].answerPairs || [{}, {}, {}, {}]).slice();
                            currentPairs[pairIndex] = { ...currentPairs[pairIndex], left: e.target.value };
                            handleSectionChange(section, 'answerPairs', currentPairs);
                          }}
                        />
                        <span className="text-white">→</span>
                        <input
                          type="text"
                          className="flex-1 p-2 bg-gray-600 text-white rounded border border-gray-500 placeholder-gray-400"
                          placeholder={`Right item ${pairIndex + 1}`}
                          defaultValue={pair.right || ''}
                          onBlur={(e) => {
                            const currentPairs = (gameForm[section].answerPairs || [{}, {}, {}, {}]).slice();
                            currentPairs[pairIndex] = { ...currentPairs[pairIndex], right: e.target.value };
                            handleSectionChange(section, 'answerPairs', currentPairs);
                          }}
                        />
                        <button
                          type="button"
                          className="bg-red-600 text-white px-2 py-1 rounded text-sm hover:bg-red-700"
                          onClick={() => {
                            const currentPairs = (gameForm[section].answerPairs || [{}, {}, {}, {}]).slice();
                            currentPairs.splice(pairIndex, 1);
                            handleSectionChange(section, 'answerPairs', currentPairs);
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                      onClick={() => {
                        const currentPairs = (gameForm[section].answerPairs || [{}, {}, {}, {}]).slice();
                        currentPairs.push({});
                        handleSectionChange(section, 'answerPairs', currentPairs);
                      }}
                    >
                      + Add Pair
                    </button>
                  </div>
                </div>
              </div>
            ) : gameForm[section].type === 'ordering' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-white mb-2 text-sm">Instruction</label>
                  <textarea
                    key={`${section}-ordering-instruction`}
                    defaultValue={gameForm[section].questionInstruction}
                    onBlur={(e) => handleSectionChange(section, 'questionInstruction', e.target.value)}
                    className="w-full p-3 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none"
                    rows="2"
                    placeholder="e.g., Arrange from smallest to largest"
                  />
                </div>
                <div>
                  <label className="block text-white mb-2 text-sm">Items to Order (one per line)</label>
                  <textarea
                    key={`${section}-ordering-items`}
                    defaultValue={gameForm[section].orderingItems}
                    onBlur={(e) => handleSectionChange(section, 'orderingItems', e.target.value)}
                    className="w-full p-3 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none"
                    rows="4"
                    placeholder="Item 1\nItem 2\nItem 3\nItem 4"
                  />
                </div>
              </div>
            ) : (
              <textarea
                key={`${section}-question-stable`}
                defaultValue={gameForm[section].question}
                onBlur={(e) => handleSectionChange(section, 'question', e.target.value)}
                className="w-full p-3 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none"
                rows="3"
                placeholder="Enter your question..."
              />
            )}
            <ImageUploader
              label="Upload Question Image (optional)"
              storagePath={`game-images/${user?.uid || 'temp'}/${tempGameIdRef.current}/${section}-question`}
              onUpload={(url) => handleSectionChange(section, 'questionImage', url)}
            />
            {gameForm[section].questionImage && (
              <div className="relative mt-2">
                <img src={gameForm[section].questionImage} alt="Question" className="max-h-32 rounded border" />
                <button
                  type="button"
                  className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
                  onClick={() => handleSectionImageRemove(section, 'questionImage')}
                >
                  ✕
                </button>
              </div>
            )}
          </div>
          <div>
            <label className="block text-white mb-2">Answer</label>
            <input
              key={`${section}-answer-stable`}
              type="text"
              defaultValue={gameForm[section].answer}
              onBlur={(e) => handleSectionChange(section, 'answer', e.target.value)}
              className="w-full p-3 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none"
              placeholder="Enter the answer..."
            />
            <ImageUploader
              label="Upload Answer Image (optional)"
              storagePath={`game-images/${user?.uid || 'temp'}/${tempGameIdRef.current}/${section}-answer`}
              onUpload={(url) => handleSectionChange(section, 'answerImage', url)}
            />
            {gameForm[section].answerImage && (
              <div className="relative mt-2">
                <img src={gameForm[section].answerImage} alt="Answer" className="max-h-32 rounded border" />
                <button
                  type="button"
                  className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
                  onClick={() => handleSectionImageRemove(section, 'answerImage')}
                >
                  ✕
                </button>
              </div>
            )}
          </div>
          
          {/* Creator Comment for this special section */}
          <div>
            <label className="block text-white mb-2">
              💡 Creator Comment (Optional)
            </label>
            <textarea
              key={`${section}-creator-comment-stable`}
              defaultValue={gameForm[section].creatorComment}
              onBlur={(e) => handleSectionChange(section, 'creatorComment', e.target.value)}
              className="w-full p-3 bg-gray-600 text-white rounded border border-gray-500 focus:border-green-500 focus:outline-none"
              rows="3"
              placeholder="Explain hints, song clues, or provide context for this specific question..."
            />
            <p className="text-sm text-gray-400 mt-1">
              Optional: Explain how song hints relate to the answer, provide context, or share inside jokes
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const AdminLayout = ({ children }) => (
    <div className="min-h-screen bg-gray-900 text-white">
      <nav className="w-full bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/admin')}>
          <div className="flex items-center">
            <Logo size={40} className="mr-2 logo-white" />
            <span className="text-white text-xl font-bold tracking-wide">Admin Dashboard</span>
          </div>
        </div>
        <div className="flex gap-6">
          <button onClick={() => navigate('/admin')} className="text-blue-200 hover:text-white font-semibold transition">Dashboard</button>
          <button onClick={() => navigate('/admin/users')} className="text-blue-200 hover:text-white font-semibold transition">Users</button>
          <button onClick={() => navigate('/admin/analytics')} className="text-blue-200 hover:text-white font-semibold transition">Analytics</button>
          <button onClick={() => navigate('/admin/support-tickets')} className="text-blue-200 hover:text-white font-semibold transition">Support</button>
          <button onClick={() => navigate('/admin/security')} className="text-blue-200 hover:text-white font-semibold transition">Security</button>
        </div>
      </nav>
      <main className="p-4">{children}</main>
      <style>{`
        .logo-white {
          filter: brightness(0) invert(1) drop-shadow(0 0 16px #fff8);
        }
      `}</style>
    </div>
  );

  const Layout = isAdminRoute ? AdminLayout : HostLayout;

  return (
    <Layout>
      <div className="min-h-screen bg-gray-900 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Create Trivia Game</h1>
            <p className="text-gray-400">Build your own trivia game for personal use or marketplace</p>
          </div>

          {/* Non-blocking banner for non-subscribers */}
          <div className="mb-6 bg-green-900/20 border border-green-600 rounded-xl p-4 text-center">
            <div className="text-green-400 font-semibold mb-2">🎉 Building is free!</div>
            <p className="text-green-200 text-sm">
              <strong>Launch your created games</strong> for $15 each, or <strong>submit to the marketplace</strong> to earn revenue from other users.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Game Info */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold text-white mb-4">Game Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-white mb-2">Game Title *</label>
                  <input
                    ref={titleInputRef}
                    key="title-input-stable"
                    type="text"
                    defaultValue={gameForm.title}
                    onBlur={(e) => {
                      setGameForm(prev => ({ ...prev, title: e.target.value }));
                    }}
                    className="w-full p-3 bg-gray-700 text-white rounded border border-gray-600 focus:border-green-500 focus:outline-none"
                    placeholder="e.g., Classic Rock Trivia"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white mb-2">Difficulty Level</label>
                  <select
                    value={gameForm.difficulty}
                    onChange={(e) => setGameForm({ ...gameForm, difficulty: e.target.value })}
                    className="w-full p-3 bg-gray-700 text-white rounded border border-gray-600 focus:border-green-500 focus:outline-none"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                    <option value="expert">Expert</option>
                  </select>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-white mb-2">Description</label>
                <textarea
                  key="description-textarea-stable"
                  defaultValue={gameForm.description}
                  onBlur={(e) => setGameForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full p-3 bg-gray-700 text-white rounded border border-gray-600 focus:border-green-500 focus:outline-none"
                  rows="3"
                  placeholder="Describe your game, themes, or special features..."
                />
              </div>

              {/* Hosting Notes */}
              <div className="mb-4">
                <div>
                  <label className="block text-white mb-2">
                    📝 Hosting Notes
                  </label>
                  <textarea
                    key="hosting-notes-textarea-stable"
                    defaultValue={gameForm.hostingNotes}
                    onBlur={(e) => setGameForm(prev => ({ ...prev, hostingNotes: e.target.value }))}
                    className="w-full p-3 bg-gray-700 text-white rounded border border-gray-600 focus:border-green-500 focus:outline-none"
                    rows="4"
                    placeholder="Instructions for hosts on how to run this game, special features, or quick reference guides..."
                  />
                  <p className="text-sm text-gray-400 mt-1">
                    Helpful notes for hosts running this game
                  </p>
                </div>
              </div>

              {/* Marketplace Options */}
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="marketplace"
                    checked={gameForm.isForMarketplace}
                    onChange={(e) => setGameForm({ ...gameForm, isForMarketplace: e.target.checked })}
                    className="mr-2"
                  />
                  <label htmlFor="marketplace" className="text-white">Submit to marketplace for other hosts to purchase</label>
                </div>

                {/* Admin-only: League Sponsored Game */}
                {isAdminRoute && (
                  <div className={`flex items-center p-3 rounded-lg border ${
                    gameForm.isLeagueSponsored 
                      ? 'bg-blue-900/20 border-blue-600' 
                      : 'bg-gray-800/50 border-gray-700'
                  }`}>
                    <input
                      type="checkbox"
                      id="leagueSponsored"
                      checked={gameForm.isLeagueSponsored}
                      onChange={(e) => setGameForm({ ...gameForm, isLeagueSponsored: e.target.checked })}
                      className="mr-2"
                    />
                    <label htmlFor="leagueSponsored" className={`font-semibold ${
                      gameForm.isLeagueSponsored ? 'text-blue-300' : 'text-white'
                    }`}>
                      🏆 League Sponsored Game
                    </label>
                    <div className={`ml-2 text-sm ${
                      gameForm.isLeagueSponsored ? 'text-blue-400' : 'text-gray-400'
                    }`}>
                      (Official league game with free integration)
                    </div>
                  </div>
                )}

                {/* Creator Limits Display */}
                {/* Creator limits removed - all users can submit to marketplace */}

                {gameForm.isForMarketplace && (
                  <div className="bg-green-900/20 border border-green-600 rounded-lg p-6">
                    <h3 className="text-green-400 font-semibold mb-4 text-lg">💰 Revenue Sharing - How You Get Paid</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                        <h4 className="font-semibold text-green-400 mb-2">🎯 Simple 50/50 Split</h4>
                        <p className="text-gray-300 text-sm mb-3">
                          You get exactly 50% of what buyers pay for your game.
                        </p>
                        <div className="bg-gray-700 rounded p-3">
          <p className="text-sm text-gray-300">
            <span className="text-white font-medium">Example:</span> If you price your game at $20 (minimum), buyers pay $35 total ($20 + $15 launch fee). You earn $10 (50% of $20), TriviaPlug gets $25 (50% of $20 + $15 launch fee).
          </p>
                        </div>
                      </div>
                      
                      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                        <h4 className="font-semibold text-blue-400 mb-2">💳 Direct Payments</h4>
                        <p className="text-gray-300 text-sm mb-3">
                          All payments go through Stripe for secure, instant processing.
                        </p>
                        <div className="bg-gray-700 rounded p-3">
                          <p className="text-sm text-gray-300">
                            <span className="text-white font-medium">Secure transactions:</span> All marketplace purchases use Stripe checkout for immediate payment processing and revenue sharing.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-blue-900/20 border border-blue-600 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-blue-400 mb-2">⚡ Key Benefits</h4>
                      <ul className="text-blue-300 text-sm space-y-1">
                        <li>• <strong>Instant payments:</strong> Revenue sharing processed immediately through Stripe</li>
                        <li>• <strong>Transparent tracking:</strong> See exactly how much you earned from each transaction</li>
                        <li>• <strong>Fair pricing:</strong> You always get exactly 50% of the rental price</li>
                        <li>• <strong>No hidden fees:</strong> Clear, upfront pricing with no surprises</li>
                      </ul>
                    </div>
                    
                    <div className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-4">
                      <h4 className="font-semibold text-yellow-400 mb-2">📋 Marketplace Terms</h4>
                      <ul className="text-yellow-300 text-sm space-y-1">
                        <li>• Trivia Plug retains 50% of all sales</li>
                        <li>• You receive 50% royalties from sales</li>
                        <li>• Trivia Plug owns the game content in perpetuity</li>
                        <li>• Games cannot be deleted once approved</li>
                        <li>• Quality control review required</li>
                      </ul>
                    </div>
                    
                    <div className="bg-purple-900/20 border border-purple-600 rounded-lg p-4">
                      <h4 className="font-semibold text-purple-400 mb-3">🎮 Game Launches vs Game Purchases</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                          <h5 className="font-semibold text-green-400 mb-2">💰 Game Purchase</h5>
                          <p className="text-gray-300 text-xs mb-2">One-time fee to rent the game</p>
                          <ul className="text-gray-400 text-xs space-y-1">
                            <li>• You get 50% revenue sharing</li>
                            <li>• Buyer rents the game for 12 hours</li>
                            <li>• This is where you earn money</li>
                          </ul>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                          <h5 className="font-semibold text-blue-400 mb-2">⚡ Game Launch</h5>
                          <p className="text-gray-300 text-xs mb-2">$15 per launch (12-hour session)</p>
                          <ul className="text-gray-400 text-xs space-y-1">
                            <li>• No revenue sharing (software fee)</li>
                            <li>• 12 hours of game access</li>
                            <li>• Goes to Trivia Plug platform</li>
                          </ul>
                        </div>
                      </div>
                      <p className="text-sm text-gray-300">
                        <span className="text-white font-medium">Important:</span> You earn money when people <strong>rent</strong> your games from the marketplace, not when they <strong>launch</strong> them. Trivia Plug owns all marketplace games and splits revenue with creators.
                      </p>
                    </div>
                  </div>
                )}

                {gameForm.isForMarketplace && (
                  <div>
                    <label className="block text-white mb-2">Price ($) *</label>
                    <input
                      type="number"
                      value={gameForm.price || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        const numValue = value === '' ? 0 : parseFloat(value);
                        setGameForm({ ...gameForm, price: isNaN(numValue) ? 0 : numValue });
                      }}
                      className="w-full p-3 bg-gray-700 text-white rounded border border-gray-600 focus:border-green-500 focus:outline-none"
                      min="20"
                      step="0.01"
                      placeholder="Minimum $20.00"
                      required
                    />
                    <p className="text-sm text-gray-400 mt-1">Minimum price for marketplace games is $20.00</p>
                  </div>
                )}
              </div>
            </div>

            {/* Categories */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold text-white mb-4">Categories *</h2>
              <p className="text-gray-400 mb-4">Select the categories featured in your game</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {availableCategories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => handleCategoryToggle(category)}
                    className={`p-3 rounded border transition-colors ${
                      gameForm.categories.includes(category)
                        ? 'bg-green-600 border-green-500 text-white'
                        : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-500'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Game Content */}
            {renderQuarter('quarter1', 'Quarter 1')}
            {renderQuarter('quarter2', 'Quarter 2')}
            {renderSpecialSection('halftime', 'Halftime Question')}
            {renderQuarter('quarter3', 'Quarter 3')}
            {renderQuarter('quarter4', 'Quarter 4')}
            {renderSpecialSection('final', 'Final Question')}
            {renderSpecialSection('tieBreaker', 'Tie Breaker')}

            {/* Submit Button */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate(isAdminRoute ? '/admin' : '/home')}
                className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded transition duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white font-bold py-3 px-6 rounded transition duration-200"
              >
                {loading ? 'Creating Game...' : gameForm.isForMarketplace ? 'Submit to Marketplace' : 'Create Game'}
              </button>
            </div>

            {message && (
              <div className={`text-center p-4 rounded-lg ${
                message.includes('✅') ? 'bg-green-900/20 border border-green-600 text-green-400' : 'bg-red-900/20 border border-red-600 text-red-400'
              }`}>
                <pre className="whitespace-pre-wrap text-sm font-sans">{message}</pre>
              </div>
            )}
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default AddGame;

